import { Customer_Requirements, Enums_RequirementCategories, Enums_RequirementTypes, Requirements_Admin } from "data/endpoints";
import IRepository from "data/IRepository";
import IAppState from "infrastructure/store/IAppState";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import RequirementSimplified from "../components/RequirementSimplified";
import RequirementSimplifiedHeader from "../components/RequirementSimplifiedHeader";
import { DataGridId } from "features/shared/components/dataGrid/DataGridId";
import IExternalFilterData from "features/shared/components/dataFilter/IExternalFilterData";
import RequirementTypesDialog from "../components/requirementTypesDialog/RequirementTypesDialog";
import DataGrid from "features/shared/components/dataGrid/DataGrid";
import RolesChecker from "features/customer/components/RolesChecker";
import {
  loadDataFilterConfig,
  loadPortalConfig,
} from "features/shared/helpers/modules";
import { useTranslation } from "react-i18next";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import RequirementTypesState from "infrastructure/store/requirementTypes/RequirementTypesState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import AlertWithButton from "features/shared/alerts/components/AlertWithButton";

interface RequirementCategory {
  code: string;
  name: string;
}

const requirementsDataFilterJson = loadDataFilterConfig(
  "requirementsDataFilter"
);
const requirementsAdminDataFilterJson = loadDataFilterConfig(
  "requirementsAdminDataFilter"
);
const portalConfig = loadPortalConfig();
const showRequirementsList = portalConfig.useFullRequirements || portalConfig.enableRequirementPresentation;

type Props = {
  forAdminUse?: boolean;
};

const RequirementsSection = ({ forAdminUse = false }: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const  requirementFilter = useInjection<IRepository<RequirementCategory>>(
    IocTypes.Repository
  );

  // store
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  const requirementTypes = useSelector<IAppState, RequirementTypesState>(
    (store) => store.requirementTypes
  ).requiremntTypes;

  // state
  const [externalFilterData, setExternalFilterData] = useState<
    IExternalFilterData<any>[]
  >([]);
  const [showTypesDialog, setShowTypesDialog] = useState(
    !showRequirementsList
  );

  // side effects
  useEffect(() => {
    if (forAdminUse || showRequirementsList) {
      getExternalFilterData();
    }
  }, [customer?.customerId, requirementTypes, forAdminUse]);

  // helpers
  const getExternalFilterData = async () => {
    if (forAdminUse) {
      const [requirementTypesAdmin, requirementCategoriesAdmin] = await Promise.all([
        requirementFilter.get({ url: Enums_RequirementTypes }),
        requirementFilter.get({ url: Enums_RequirementCategories }),
      ]);

      setExternalFilterData([
        {
          name: "RequirementTypeCode",
          data: requirementTypesAdmin ?? [],
        },
        {
          name: "RequirementCategoryCode",
          data: requirementCategoriesAdmin ?? [],
        },
      ]);
      return;
    }

    if (!customer?.customerId) {
      setExternalFilterData([]);
      return;
    }

    const requirementCategories = await requirementFilter.get({
      url: Enums_RequirementCategories,
      params: new URLSearchParams({ customerId: customer.customerId }),
    });

    let externalFilterData: IExternalFilterData<any>[] = [];

    // RequirementType
    externalFilterData.push({
      name: "RequirementTypeCode",
      data: requirementTypes,
    });

    // RequirementCategory
    externalFilterData.push({
      name: "RequirementCategoryCode",
      data: requirementCategories ?? [],
    });

    setExternalFilterData(externalFilterData);
  };

  // handlers
  const openRequirementTypesDialogHandler = async () => {
    setShowTypesDialog(true);
  };

  // sub-renders
  const renderOpenTypeDialogBtn = (): JSX.Element => {
    return (
      <RolesChecker enabledRoles="Contributor">
        <button
          className="btn btn-sm btn-secondary"
          onClick={openRequirementTypesDialogHandler}
        >
          {tLabels("requirementsSection.createRequest")}
        </button>
      </RolesChecker>
    );
  };

  const renderNoRoleAlert = (): JSX.Element => { 
    return (
      <AlertWithButton 
        alertInfo={tLabels("requirementsSection.noPermissionsToCreate")}
      />
    );
  }

  // render
  if (forAdminUse) {
    return (
      <DataGrid
        gridId={DataGridId.requirementAdminGrid}
        url={Requirements_Admin}
        routeParams={new URLSearchParams()}
        filterJson={requirementsAdminDataFilterJson}
        filterExternalData={externalFilterData}
        renderItem={(item) => <RequirementSimplified requirement={item} forAdminUse />}
        renderHeader={() => <RequirementSimplifiedHeader forAdminUse />}
        renderNoContent={() => (
          <AlertWithButton alertInfo={tLabels("requirementsSection.requirementsNotFound")} />
        )}
      />
    );
  }

  return (
    <>
      <RenderWrapper isVisible={() => showRequirementsList}>
        <DataGrid
          gridId={DataGridId.requirementGrid}
          url={Customer_Requirements}
          filterJson={requirementsDataFilterJson}
          filterExternalData={externalFilterData}
          renderItem={(item) => <RequirementSimplified requirement={item} />}
          renderHeader={() => <RequirementSimplifiedHeader />}
          renderGridHeaderBtns={renderOpenTypeDialogBtn}
          renderNoContent={() => (
            <AlertWithButton 
              alertInfo={tLabels("requirementsSection.requirementsNotFound")}
              renderButton={renderOpenTypeDialogBtn} 
            />
          )}
        />
      </RenderWrapper>
      <RenderWrapper isVisible={() => !showRequirementsList}>
        <RolesChecker
          enabledRoles="Contributor"
          unauthorizedRender={renderNoRoleAlert}
        >
          <AlertWithButton 
            alertInfo={tLabels("requirementsSection.selectRequirement")}
            renderButton={renderOpenTypeDialogBtn} 
          />
        </RolesChecker>
      </RenderWrapper>
      <RequirementTypesDialog
        show={showTypesDialog}
        setShow={setShowTypesDialog}
        requirementTypes={requirementTypes}
      />
    </>
  );
};

export default RequirementsSection;
